import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@foundation/components/ui/card";
import { Button } from "@foundation/components/ui/button";
import { Badge } from "@foundation/components/ui/badge";
import { Input } from "@foundation/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@foundation/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@foundation/components/ui/dialog";
import { Progress } from "@foundation/components/ui/progress";
import {
  ArrowLeft,
  Upload,
  FileText,
  Search,
  Eye,
  Download,
  X,
  Loader2,
  Library,
} from "lucide-react";
import { useToast } from "@foundation/hooks/use-toast";
import { useUser } from "@foundation/contextapi/UserContext";
import { offlineSupabase } from "@foundation/utils/offlineSupabaseProxy";
import { enhancedClientService } from "../services/enhancedClientService";
import { createRagStore, uploadToRagStore } from "../services/quickCompareService";
import { supabase } from "@foundation/utils/supabaseClient";
import { supabaseRevisr } from "../utils/supabaseRevisrClient";
import { API_URL, API_KEY } from "../lib/constants";
import axios from "axios";
import ScopingAILayout from "../components/RevisrLayout";

interface Template {
  id: string;
  name: string;
  version: string;
  sections: number;
  description: string;
  category: string;
  industry: string;
  lastUpdated: string;
  downloads: number;
  filepath?: string | null;
  fileType?: string;
  fileSize?: number;
}

export default function TemplateLibraryPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useUser();

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedIndustry, setSelectedIndustry] = useState("all");
  
  // User context and organization
  const [orgId, setOrgId] = useState<string | undefined>(undefined);
  const [storeId, setStoreId] = useState<string | null>(null);
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);

  // Upload functionality state
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ current: number; total: number; message: string } | null>(null);

  // Preview state
  const [previewFile, setPreviewFile] = useState<{ url: string; name: string; type: string } | null>(null);

  // Standard document data - fetched from database
  const [templates, setTemplates] = useState<Template[]>([]);

  // Dynamic categories and industries from templates
  const categories = ["all", ...Array.from(new Set(templates.map(t => t.category).filter(Boolean)))];
  const industries = ["all", ...Array.from(new Set(templates.map(t => t.industry).filter(Boolean)))];

  // Get organization ID
  useEffect(() => {
    const getOrgId = async () => {
      if (!user?.id) return;

      try {
        const { data, error } = await offlineSupabase
          .rpc("get_user_organizations", { p_user_id: user.id });

        if (error) {
          console.warn('Could not get organizations via RPC:', error);
          try {
            const orgIds = await enhancedClientService.getUserOrganizations();
            if (orgIds && orgIds.length > 0) {
              setOrgId(orgIds[0]);
              return;
            }
          } catch (fallbackErr) {
            console.warn('Fallback method also failed:', fallbackErr);
          }
          return;
        }

        if (data && data.length > 0) {
          setOrgId(data[0].id);
        } else {
          try {
            const orgIds = await enhancedClientService.getUserOrganizations();
            if (orgIds && orgIds.length > 0) {
              setOrgId(orgIds[0]);
            }
          } catch (fallbackErr) {
            console.warn('Fallback method also failed:', fallbackErr);
          }
        }
      } catch (err) {
        console.warn('Could not get orgId:', err);
        try {
          const orgIds = await enhancedClientService.getUserOrganizations();
          if (orgIds && orgIds.length > 0) {
            setOrgId(orgIds[0]);
          }
        } catch (fallbackErr) {
          console.warn('All methods to get orgId failed:', fallbackErr);
        }
      }
    };

    getOrgId();
  }, [user?.id]);

  // Load documents from database
  useEffect(() => {
    const loadDocuments = async () => {
      if (!user?.id || !orgId) return;

      setIsLoadingDocuments(true);
      try {
        const response = await axios.get(`${API_URL}/api/revisr/templates/standard-documents`, {
          headers: {
            'x-api-key': API_KEY,
            'x-user-id': user.id,
            'x-org-id': orgId,
          },
        });

        const dbDocuments = response.data.documents || [];
        
        // Transform database documents to Template format
        const transformedTemplates: Template[] = dbDocuments.map((doc: any) => ({
          id: doc.doc_name,
          name: doc.filename || doc.doc_name,
          version: "Custom",
          sections: 0,
          description: `Standard document - ${doc.fileType || 'document'}`,
          category: doc.category || "Standard",
          industry: "All Industries",
          lastUpdated: doc.uploadedAt ? new Date(doc.uploadedAt).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
          downloads: 0,
          filepath: doc.filepath,
          fileType: doc.fileType,
          fileSize: doc.fileSize,
        }));

        setTemplates(transformedTemplates);
      } catch (error) {
        console.error('Error loading documents:', error);
        toast({
          title: "Error Loading Documents",
          description: "Could not load standard documents from database.",
          variant: "destructive",
        });
      } finally {
        setIsLoadingDocuments(false);
      }
    };

    loadDocuments();
  }, [user?.id, orgId, toast]);

  // Get or create standard document store
  const getOrCreateTemplateStore = async (): Promise<string> => {
    const storedStoreId = localStorage.getItem('templateLibrary_storeId');
    if (storedStoreId) {
      return storedStoreId;
    }

    if (!user?.id || !orgId) {
      throw new Error('User context required. Please ensure you are logged in.');
    }

    const storeName = `template-library-${user.email?.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase() || 'default'}`;
    const newStoreId = await createRagStore(storeName, 'langgraph', user.id, orgId);
    
    localStorage.setItem('templateLibrary_storeId', newStoreId);
    return newStoreId;
  };

  // Filter standard documents based on search and filters
  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         template.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === "all" || template.category === selectedCategory;
    const matchesIndustry = selectedIndustry === "all" || template.industry === selectedIndustry;
    
    return matchesSearch && matchesCategory && matchesIndustry;
  });

  const handlePreviewTemplate = async (template: Template) => {
    if (!template.filepath) {
      toast({
        title: "Preview Unavailable",
        description: "File path not found for this document.",
        variant: "destructive",
      });
      return;
    }

    try {
      const { data, error } = await supabaseRevisr.storage
        .from('knowledge_docs')
        .download(template.filepath);

      if (error) throw error;

      const blobUrl = URL.createObjectURL(data);
      const fileType = template.fileType || 'application/pdf';
      
      setPreviewFile({
        url: blobUrl,
        name: template.name,
        type: fileType,
      });
    } catch (error) {
      toast({
        title: "Preview Failed",
        description: "Could not load document preview.",
        variant: "destructive",
      });
    }
  };

  const closePreview = () => {
    if (previewFile?.url) {
      URL.revokeObjectURL(previewFile.url);
    }
    setPreviewFile(null);
  };

  const handleDownloadTemplate = async (template: Template) => {
    if (!template.filepath) {
      toast({
        title: "Download Unavailable",
        description: "File path not found for this document.",
        variant: "destructive",
      });
      return;
    }

    try {
      const { data, error } = await supabaseRevisr.storage
        .from('knowledge_docs')
        .download(template.filepath);

      if (error) throw error;

      const blob = new Blob([data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = template.name || 'document';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast({
        title: "Download Started",
        description: `${template.name} is being downloaded.`,
      });
    } catch (error) {
      toast({
        title: "Download Failed",
        description: "Could not download document.",
        variant: "destructive",
      });
    }
  };

  // File upload handlers
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const validateFile = (file: File): string | null => {
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain',
      'application/rtf',
      'application/vnd.oasis.opendocument.text'
    ];

    const maxSize = 50 * 1024 * 1024; // 50MB

    if (!allowedTypes.includes(file.type)) {
      return "Please upload a valid document file (PDF, DOC, DOCX, TXT, RTF, ODT)";
    }

    if (file.size > maxSize) {
      return "File size must be less than 50MB";
    }

    return null;
  };

  const handleFileSelect = (files: FileList | File[]) => {
    const file = files[0];
    if (!file) return;

    const error = validateFile(file);
    if (error) {
      toast({
        title: "Invalid File",
        description: error,
        variant: "destructive",
      });
      return;
    }

    setUploadedFile(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files);
    }
  };

  const handleUploadSubmit = async () => {
    if (!uploadedFile) {
      toast({
        title: "No File Selected",
        description: "Please select a file to upload.",
        variant: "destructive",
      });
      return;
    }

    if (!user?.id) {
      toast({
        title: "Authentication Required",
        description: "Please ensure you are logged in.",
        variant: "destructive",
      });
      return;
    }

    if (!orgId) {
      toast({
        title: "Organization Required",
        description: "Unable to determine your organization. Please refresh the page.",
        variant: "destructive",
      });
      return;
    }

    setIsUploading(true);
    setUploadProgress({ current: 0, total: 3, message: 'Initializing upload...' });

    try {
      // Step 1: Get or create store
      setUploadProgress({ current: 1, total: 3, message: 'Preparing storage...' });
      let currentStoreId = storeId;
      if (!currentStoreId) {
        currentStoreId = await getOrCreateTemplateStore();
        setStoreId(currentStoreId);
      }

      // Step 2: Upload file to RAG store
      setUploadProgress({ current: 2, total: 3, message: `Uploading and processing ${uploadedFile.name}...` });
      await uploadToRagStore(
        currentStoreId,
        uploadedFile,
        'standard',
        'langgraph',
        user.id,
        orgId
      );

      // Step 3: Complete
      setUploadProgress({ current: 3, total: 3, message: 'Upload complete!' });

      toast({
        title: "Upload Successful",
        description: `Standard document "${uploadedFile.name}" has been uploaded, parsed, and stored successfully.`,
      });

      // Reload documents from database
      const response = await axios.get(`${API_URL}/api/revisr/templates/standard-documents`, {
        headers: {
          'x-api-key': API_KEY,
          'x-user-id': user.id,
          'x-org-id': orgId,
        },
      });

      const dbDocuments = response.data.documents || [];
      const transformedTemplates: Template[] = dbDocuments.map((doc: any) => ({
        id: doc.doc_name,
        name: doc.filename || doc.doc_name,
        version: "Custom",
        sections: 0,
        description: `Standard document - ${doc.fileType || 'document'}`,
        category: doc.category || "Standard",
        industry: "All Industries",
        lastUpdated: doc.uploadedAt ? new Date(doc.uploadedAt).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        downloads: 0,
        filepath: doc.filepath,
        fileType: doc.fileType,
        fileSize: doc.fileSize,
      }));

      setTemplates(transformedTemplates);

      // Reset state
      setUploadedFile(null);
      setUploadProgress(null);

    } catch (error) {
      console.error('Upload error:', error);
      let errorMessage = "There was an error uploading your standard document. Please try again.";
      
      if (error instanceof Error) {
        if (error.message === 'STORE_NOT_FOUND') {
          errorMessage = "Storage not found. Please try again.";
          localStorage.removeItem('templateLibrary_storeId');
          setStoreId(null);
        } else if (error.message.includes('Network Error') || error.message.includes('ERR_CONNECTION_REFUSED')) {
          errorMessage = "Server not available. Please ensure the backend server is running.";
        } else {
          errorMessage = error.message;
        }
      }
      
      toast({
        title: "Upload Failed",
        description: errorMessage,
        variant: "destructive",
      });
      setUploadProgress(null);
    } finally {
      setIsUploading(false);
    }
  };


  return (
    <ScopingAILayout>
      <div className="min-h-screen bg-background overflow-x-hidden">
                  {/* Back Button */}
                  <div className="flex justify-start mt-8">
            <Button
              variant="ghost"
              onClick={() => navigate("/revisr/resource-library")}
              className="flex items-center space-x-2"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Back to Reference Library</span>
            </Button>
          </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-primary/10 p-3 rounded-xl">
                <Library className="h-6 w-6 text-primary" />
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground">
                Revisr Library
              </h1>
            </div>
            <p className="text-muted-foreground max-w-2xl text-lg mb-8">
              Browse our comprehensive library of preloaded standard documents, standards and reference materials.
            </p>
          </div>

          {/* Upload Section - Inline (Smaller) */}
          <Card className="mb-6">
            <CardContent className="pt-4">
              <div
                className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                  dragActive
                    ? "border-primary bg-primary/10"
                    : "border-muted-foreground/25 hover:border-primary/50"
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <Upload className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                <h3 className="text-lg font-semibold text-foreground mb-1">Upload File(s)</h3>
                <p className="text-xs text-muted-foreground mb-4">
                  Select files to upload to your knowledge base
                </p>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,.txt,.md,.rtf,.odt"
                  onChange={handleFileInputChange}
                  className="hidden"
                  id="template-file-input"
                  disabled={isUploading}
                />
                <Button
                  variant="outline"
                  onClick={() => document.getElementById('template-file-input')?.click()}
                  disabled={isUploading}
                  size="sm"
                  className="mb-3"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Choose Files
                </Button>
                <p className="text-xs text-muted-foreground">
                  Supports: PDF, DOC, DOCX, TXT, MD, RTF, ODT (Max 50MB)
                </p>

                {/* Selected File Preview */}
                {uploadedFile && (
                  <div className="mt-4 p-3 bg-primary/5 rounded-lg border border-primary/20 max-w-md mx-auto">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-primary" />
                        <div className="text-left">
                          <p className="text-sm font-medium text-foreground">{uploadedFile.name}</p>
                          <p className="text-xs text-muted-foreground">{formatFileSize(uploadedFile.size)}</p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setUploadedFile(null)}
                        className="text-destructive hover:text-destructive"
                        disabled={isUploading}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}

                {/* Upload Progress */}
                {uploadProgress && (
                  <div className="mt-4 space-y-2 max-w-md mx-auto">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">{uploadProgress.message}</span>
                      <span className="text-muted-foreground">
                        {uploadProgress.current}/{uploadProgress.total}
                      </span>
                    </div>
                    <Progress 
                      value={(uploadProgress.current / uploadProgress.total) * 100} 
                      className="w-full h-2"
                    />
                  </div>
                )}

                {/* Upload Button */}
                {uploadedFile && !isUploading && (
                  <div className="mt-4">
                    <Button
                      onClick={handleUploadSubmit}
                      size="sm"
                      className="bg-primary text-primary-foreground hover:bg-primary/90"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Upload Standard Document
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Search and Filter Section */}
          {templates.length > 0 && (
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="text-xl flex items-center">
                  <Search className="h-5 w-5 mr-2" />
                  Search Standard Documents
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Search Input */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                  <Input
                    placeholder="Search standard documents..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Standard Documents Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-fr">
            {filteredTemplates.map((template) => (
              <motion.div
                key={template.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="flex"
              >
                <Card className="group relative overflow-hidden border border-primary/10 hover:border-primary/30 transition-all duration-300 hover:shadow-lg flex flex-col w-full h-full">
                  <CardHeader className="pb-3 flex-shrink-0">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-lg font-semibold group-hover:text-primary transition-colors duration-300 break-words">
                          {template.name}
                        </CardTitle>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <Badge variant="outline" className="text-xs">
                            v{template.version}
                          </Badge>
                          <Badge variant="secondary" className="text-xs">
                            {template.category}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                      {template.description}
                    </p>
                  </CardHeader>

                  <CardContent className="pt-0 flex-1 flex flex-col justify-between">
                    <div className="space-y-3">
                      {/* Standard Document Stats */}
                      <div className="flex items-center justify-between text-xs text-muted-foreground flex-wrap gap-1">
                        <span>{template.sections} sections</span>
                        <span>{template.downloads} downloads</span>
                        <span>Updated {template.lastUpdated}</span>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex gap-2 justify-end mt-auto pt-2">
                        <Button
                          onClick={() => handlePreviewTemplate(template)}
                          variant="outline"
                          size="sm"
                          disabled={!template.filepath}
                          className="flex-shrink-0"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          onClick={() => handleDownloadTemplate(template)}
                          variant="outline"
                          size="sm"
                          disabled={!template.filepath}
                          className="flex-shrink-0"
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* No Results */}
          {!isLoadingDocuments && filteredTemplates.length === 0 && (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">No standard documents found</h3>
              <p className="text-muted-foreground">
                Try adjusting your search criteria or upload a custom standard document.
              </p>
            </div>
          )}

          {/* Loading State */}
          {isLoadingDocuments && (
            <div className="text-center py-12">
              <Loader2 className="h-12 w-12 text-muted-foreground mx-auto mb-4 animate-spin" />
              <h3 className="text-lg font-medium text-foreground mb-2">Loading standard documents...</h3>
            </div>
          )}
        </div>

        {/* Preview Dialog */}
        <Dialog open={!!previewFile} onOpenChange={closePreview}>
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5" />
                Preview: {previewFile?.name}
              </DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-auto min-h-0">
              {previewFile && (
                <>
                  {previewFile.type === 'application/pdf' || previewFile.type.includes('pdf') ? (
                    <iframe
                      src={previewFile.url}
                      className="w-full h-[75vh] border-0"
                      title="Document Preview"
                      style={{ border: 'none', display: 'block' }}
                    />
                  ) : previewFile.type.includes('text') || previewFile.type.includes('plain') ? (
                    <div className="p-4 bg-background">
                      <iframe
                        src={previewFile.url}
                        className="w-full h-[75vh] border-0"
                        title="Document Preview"
                        style={{ border: 'none', display: 'block' }}
                      />
                    </div>
                  ) : (
                    <div className="p-8 text-center">
                      <FileText className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
                      <p className="text-muted-foreground">
                        Preview not available for this file type. Please download to view.
                      </p>
                      <Button
                        onClick={() => {
                          const a = document.createElement('a');
                          a.href = previewFile.url;
                          a.download = previewFile.name;
                          document.body.appendChild(a);
                          a.click();
                          document.body.removeChild(a);
                        }}
                        className="mt-4"
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Download File
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </ScopingAILayout>
  );
}
